import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    numberOfTabs = "numberOfTabs",
}

interface LinkModel {
    fields: {
        destination: LinkFieldValue;
        overrideTitle: string;
        title: string;
        children?: LinkModel[];
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { links },
    } = ownProps;
    const mappedData = [];
    for (const link of links) {
        const {
            fields: { destination, overrideTitle },
        } = link;
        const { type, value } = destination;

        const categoryState = getCategoryState(state, value);
        mappedData.push(categoryState);
    }
    return {
        mappedData,
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface HomePageProductCategoryStyles {
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    titleText?: InjectableCss;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
}

export const homePageProductCategoryStyles: HomePageProductCategoryStyles = {
    container: {
        gap: 20,

        css: css`
            margin-bottom: 18px;
            padding: 0 10px;
            > div[class^="GridOffset"] {
                justify-content: center;
            }
        `,
    },
    titleText: {
        css: css`
            letter-spacing: 0px;
            text-transform: capitalize;
            opacity: 1;
            font-size: 2rem;
            font-weight: bold;
            line-height: 1;
            margin-bottom: 1rem;
            padding-top: 36px;
            text-align: center;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 25px;
                            padding-top: 30px;
                            font-size: 30px;
                        `,
                        css`
                            margin-bottom: 25px;
                            padding-top: 30px;
                            font-size: 30px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    categoryItem: {
        width: [12, 6, 4, 2, 2],
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
        `,
    },
    innerContainer: {
        gap: 10,
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
        `,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    categoryImage: {
        width: "127px",
        height: "127px",
        css: css`
            border-radius: 50%;
            opacity: 1;
            z-index: 98;
            background: #f3efeb;

            img {
                z-index: 99;
                height: 100% !important;
            }
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    categoryName: {
        css: css`
            color: ${getColor("primary")};
            font-size: 14px;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
};

const styles = homePageProductCategoryStyles;

const GAIAHomeProductCategories: React.FC<Props> = ({ fields, mappedData }) => {
    return (
        <StyledWrapper>
            <Typography as="h4" {...styles.titleText}>
                Product Categories
            </Typography>
            <GridContainer {...styles.container}>
                {mappedData.map((destinationLink, index) => (
                    <>
                        {destinationLink.value && (
                            <GridItem key={`${index}-${index}`} {...styles.categoryItem}>
                                <Link href={destinationLink.value?.path} {...styles.categoryImageLink}>
                                    <GridContainer {...styles.innerContainer}>
                                        <GridItem {...styles.categoryImageItem}>
                                            {destinationLink.value?.path && (
                                                <LazyImage
                                                    src={destinationLink.value?.smallImagePath}
                                                    className="category-image"
                                                    {...styles.categoryImage}
                                                />
                                            )}
                                        </GridItem>
                                        <GridItem {...styles.categoryNameLinkItem}>
                                            <Typography {...styles.categoryName}>
                                                {fields.links[index].fields.overrideTitle ||
                                                    destinationLink.value?.shortDescription}
                                            </Typography>
                                        </GridItem>
                                    </GridContainer>
                                </Link>
                            </GridItem>
                        )}
                    </>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(GAIAHomeProductCategories),
    definition: {
        group: "Categories",
        displayName: "GAIA HomePage Category List",
        allowedContexts: ["HomePage"],
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overrideTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overrideTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overrideTitle) {
                            return overrideTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Category",
                        },
                        isRequired: true,
                        allowUrls: item => item.fields.linkType === "Link",
                    },
                    {
                        name: "overrideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.destination.type !== "Url",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
